import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useError } from '../../contexts/ErrorContext';
import InventoryLayout from './InventoryLayout';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import StockOutForm from './StockOutForm';
import InventoryBackButton from './InventoryBackButton';
import { useProducts } from './hooks/useProducts';
import { useInventory } from './hooks/useInventory';
import { getPlatformClass } from '../../utils/platformUtils';
import { auth } from '../../firebaseConfig';

function StockOutPage() {
  const navigate = useNavigate();
  const platformClass = getPlatformClass();
  const isMobilePlatform = platformClass.includes('ios') || platformClass.includes('android') || platformClass.includes('pwa');
  const { showErrorToast } = useError();

  // Use Firebase hooks
  const {
    products,
    loading: isLoading,
    error,
    clearErrors
  } = useProducts();
  
  const { performStockOut } = useInventory();

  // Handle stock out save
  const handleStockOutSave = async (stockOutData) => {
    try {
      // Use the useInventory hook to perform stock out operation
      // This will update the product stock AND create a stock movement record
      await performStockOut({
        ...stockOutData,
        createdBy: auth.currentUser?.uid || 'unknown'
      });

      // Show success message
      const productName = stockOutData.variantName 
        ? `${stockOutData.productName} - ${stockOutData.variantName}`
        : stockOutData.productName;
      showErrorToast(`Successfully removed ${stockOutData.quantity} ${productName} from inventory!`, {
        variant: 'info',
        autoHide: true,
        duration: 3000
      });
      
      // Navigate back to inventory dashboard
      navigate('/inventory');
    } catch (error) {
      showErrorToast(`Failed to remove stock: ${error.message}`, {
        variant: 'error',
        autoHide: true,
        duration: 5000
      });
      throw error;
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/inventory');
  };

  // Render content based on state
  const renderContent = () => {
    if (error) {
      return (
        <div className="inventory-error">
          <PlatformText variant="h3">Error Loading Products</PlatformText>
          <PlatformText variant="body">{error}</PlatformText>
          <PlatformButton onClick={clearErrors}>Retry</PlatformButton>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="inventory-loading">
          <PlatformText variant="h3">Loading products...</PlatformText>
        </div>
      );
    }

    return (
      <>
        {/* Stock Out Form */}
        <StockOutForm
          products={products}
          onSave={handleStockOutSave}
          onCancel={handleCancel}
        />
      </>
    );
  };

  return (
    <InventoryLayout>
      <div className={`inventory-container ${platformClass}`}>
        {/* Mobile App Bar */}
        {isMobilePlatform && (
          <div className="mobile-app-bar inventory-mobile-app-bar">
            <InventoryBackButton onClick={() => navigate('/inventory')}>Stock Out</InventoryBackButton>
          </div>
        )}

        {/* Desktop Header */}
        {!isMobilePlatform && (
          <div className="inventory-page-header">
            <div>
              <InventoryBackButton onClick={() => navigate('/inventory')}>Stock Out</InventoryBackButton>
              <PlatformText variant="body" className="inventory-page-subtitle">
                Issue stock from your inventory
              </PlatformText>
            </div>
          </div>
        )}

        {renderContent()}
      </div>
    </InventoryLayout>
  );
}

export default StockOutPage; 